import { useEffect, useRef } from 'react';

import { timeMeasurement } from '@pkg/utils';

export type ExpireOption = 'hour' | 'day' | 'halfWeek' | 'week' | 'month';
type ExpireFromNow = ExpireOption | number;
type ShorthandOptions = Record<ExpireOption, string>;

export const EXPIRE_KEYS: ExpireOption[] = ['hour', 'day', 'halfWeek', 'week', 'month'];
export const EXPIRE_INCREMENTS: Record<ExpireOption, number> = {
    hour: timeMeasurement.msPerHr,
    day: timeMeasurement.msPerDay,
    halfWeek: timeMeasurement.msPerDay * 3,
    week: timeMeasurement.msPerWeek,
    month: timeMeasurement.msPerMonth,
};
const EXPIRE_SHORTHAND: ShorthandOptions = {
    hour: '1 hour',
    day: '1 day',
    halfWeek: '3 days',
    week: '7 days',
    month: '1 month',
};

function getPastPresentFutureLabel(value = 0) {
    // TODO: If we decide to keep the `ms` functionality for
    // this hook, then each of these labels need `i18n`.
    if (value === 0) return 'now';
    return value > 0 ? 'future' : 'past';
}

function getDateFromNow(fromNow: ExpireFromNow) {
    const increment = typeof fromNow === 'string' ? EXPIRE_INCREMENTS[fromNow] : fromNow;
    return new Date(Date.now() + increment);
}

export function useExpireTimestamp(fromNow: ExpireFromNow, shorthand: Partial<ShorthandOptions> = {}) {
    const nowDate = useRef(getDateFromNow(fromNow));

    useEffect(() => {
        nowDate.current = getDateFromNow(fromNow);
    }, [fromNow]);

    const predeterminedShorthand: ShorthandOptions = {
        ...EXPIRE_SHORTHAND,
        ...shorthand,
    };

    // TODO: Improve the formatting to match the designs.
    return {
        expireDate: nowDate.current,
        expireLabel: [nowDate.current.toDateString(), nowDate.current.toLocaleTimeString()].join(', '),
        expireShorthand:
            typeof fromNow === 'string' ? predeterminedShorthand[fromNow] : getPastPresentFutureLabel(fromNow),
    };
}
